import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Modal,
  Space,
  Typography,
  Divider,
  message,
  DatePicker,
  Upload,
  Timeline,
  Spin,
  Popconfirm,
  Image,
  Tag,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import {
  EditOutlined,
  DollarOutlined,
  DeleteOutlined,
  FileImageOutlined,
  HistoryOutlined,
  CloudUploadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bankTransactionsApi } from '../../services/api';
import dayjs from 'dayjs';
import { colors, spacing } from '../design-system/tokens';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface BankTransaction {
  id: string;
  transactionType: string;
  amount: number;
  date: string;
  reference: string | null;
  counterparty: string | null;
  description: string | null;
  status: string;
  checkImageDriveId?: string | null;
  checkImageUrl?: string | null;
  checkImageName?: string | null;
  xeroPaymentId?: string | null;
  allocations?: Array<{
    id: string;
    amount: number;
    shipment?: { reference: string };
  }>;
}

interface HistoryEntry {
  id: string;
  action: string;
  changes: Record<string, { old: any; new: any }>;
  createdAt: string;
  user: { id: string; email: string; name: string } | null;
}

interface CheckEditModalProps {
  transaction: BankTransaction;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CheckEditModal: React.FC<CheckEditModalProps> = ({
  transaction,
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [activeTab, setActiveTab] = useState<'edit' | 'history'>('edit');
  const queryClient = useQueryClient();

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['bank-transaction-history', transaction.id],
    queryFn: () => bankTransactionsApi.getHistory(transaction.id),
    enabled: open && activeTab === 'history',
  });

  useEffect(() => {
    if (open && transaction) {
      form.setFieldsValue({
        reference: transaction.reference,
        amount: Number(transaction.amount),
        date: dayjs(transaction.date),
        counterparty: transaction.counterparty,
        description: transaction.description,
      });
      setFileList([]);
    }
  }, [open, transaction, form]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => bankTransactionsApi.update(transaction.id, data),
    onSuccess: () => {
      message.success('Check updated successfully');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transaction-history', transaction.id] });
      onSuccess?.();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update check');
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: (data: { imageBase64: string; filename: string; mimeType?: string }) =>
      bankTransactionsApi.uploadCheckImage(transaction.id, data),
    onSuccess: () => {
      message.success('Check image uploaded');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transaction-history', transaction.id] });
      setFileList([]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to upload image');
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: () => bankTransactionsApi.deleteCheckImage(transaction.id),
    onSuccess: () => {
      message.success('Check image deleted');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transaction-history', transaction.id] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete image');
    },
  });

  const pushToXeroMutation = useMutation({
    mutationFn: () => bankTransactionsApi.pushToXero(transaction.id),
    onSuccess: () => {
      message.success('Payment synced to Xero');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to sync to Xero');
    },
  });

  const handleSubmit = async (values: any) => {
    const updateData = {
      reference: values.reference,
      amount: values.amount,
      date: values.date.toISOString(),
      counterparty: values.counterparty,
      description: values.description || null,
    };
    updateMutation.mutate(updateData);
  };

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleUploadImage = async () => {
    const uploadFile = fileList[0];
    if (!uploadFile?.originFileObj) return;

    const file = uploadFile.originFileObj;
    const fileName = file.name || 'check-image';
    const fileType = file.type || 'image/jpeg';
    const reader = new FileReader();
    
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] || result;
      uploadImageMutation.mutate({
        imageBase64: base64,
        filename: fileName,
        mimeType: fileType,
      });
    };
    
    reader.readAsDataURL(file);
  };

  const formatAction = (action: string): string => {
    const actionMap: Record<string, string> = {
      'transaction_updated': 'Updated',
      'check_image_uploaded': 'Image Uploaded',
      'check_image_replaced': 'Image Replaced',
      'check_image_deleted': 'Image Deleted',
    };
    return actionMap[action] || action;
  };

  const formatChanges = (changes: Record<string, { old: any; new: any }>) => {
    return Object.entries(changes).map(([field, { old: oldVal, new: newVal }]) => (
      <div key={field} style={{ marginLeft: 8 }}>
        <Text type="secondary">{field}: </Text>
        <Text delete type="secondary">{String(oldVal ?? 'empty')}</Text>
        <Text> → </Text>
        <Text>{String(newVal ?? 'empty')}</Text>
      </div>
    ));
  };

  const history = (historyData as any)?.history || [];
  const isPushedToXero = !!transaction.xeroPaymentId;

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          Edit Check #{transaction.reference || transaction.id.slice(-6)}
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
    >
      <Space style={{ marginBottom: spacing.md }}>
        <Button
          type={activeTab === 'edit' ? 'primary' : 'default'}
          onClick={() => setActiveTab('edit')}
          icon={<EditOutlined />}
        >
          Edit
        </Button>
        <Button
          type={activeTab === 'history' ? 'primary' : 'default'}
          onClick={() => setActiveTab('history')}
          icon={<HistoryOutlined />}
        >
          History
        </Button>
      </Space>

      {activeTab === 'edit' ? (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {isPushedToXero && (
            <div style={{ 
              padding: spacing.sm, 
              backgroundColor: '#fff7e6', 
              borderRadius: 4,
              marginBottom: spacing.md,
              border: '1px solid #ffd591'
            }}>
              <Text type="warning">
                This transaction has been pushed to Xero. Changes will not automatically sync.
                Use "Re-sync to Xero" after making changes.
              </Text>
            </div>
          )}

          <Space size="middle" style={{ display: 'flex', marginBottom: 0 }}>
            <Form.Item
              label="Check Number"
              name="reference"
              rules={[{ required: true, message: 'Please enter check number' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="e.g., 1234" />
            </Form.Item>

            <Form.Item
              label="Amount (USD)"
              name="amount"
              rules={[
                { required: true, message: 'Please enter amount' },
                { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber
                prefix={<DollarOutlined />}
                style={{ width: '100%' }}
                precision={2}
                min={0.01}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value!.replace(/,/g, '') as any}
              />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ display: 'flex', marginBottom: 0 }}>
            <Form.Item
              label="Check Date"
              name="date"
              rules={[{ required: true, message: 'Please select date' }]}
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item
              label="Received From"
              name="counterparty"
              rules={[{ required: true, message: 'Please enter payer' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="Buyer name" />
            </Form.Item>
          </Space>

          <Form.Item
            label="Notes"
            name="description"
          >
            <TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>

          <Divider />

          <Title level={5}>Check Image</Title>
          
          {transaction.checkImageUrl ? (
            <div style={{ marginBottom: spacing.md }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 4, 
                  padding: spacing.sm,
                  backgroundColor: '#fafafa'
                }}>
                  <Space>
                    <FileImageOutlined style={{ fontSize: 24, color: colors.primary[60] }} />
                    <div>
                      <Text strong>{transaction.checkImageName || 'Check Image'}</Text>
                      <br />
                      <a href={transaction.checkImageUrl} target="_blank" rel="noopener noreferrer">
                        View in Google Drive
                      </a>
                    </div>
                  </Space>
                </div>
                <Popconfirm
                  title="Delete check image?"
                  description="This action cannot be undone."
                  onConfirm={() => deleteImageMutation.mutate()}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button 
                    danger 
                    icon={<DeleteOutlined />}
                    loading={deleteImageMutation.isPending}
                  >
                    Remove Image
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          ) : (
            <div style={{ marginBottom: spacing.md }}>
              <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={handleUploadChange}
                beforeUpload={() => false}
                maxCount={1}
                accept="image/*,.pdf"
              >
                {fileList.length < 1 && (
                  <div>
                    <FileImageOutlined style={{ fontSize: 24 }} />
                    <div style={{ marginTop: 8 }}>Select Image</div>
                  </div>
                )}
              </Upload>
              {fileList.length > 0 && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  onClick={handleUploadImage}
                  loading={uploadImageMutation.isPending}
                  style={{ marginTop: spacing.sm }}
                >
                  Upload to Google Drive
                </Button>
              )}
            </div>
          )}

          <Divider />

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateMutation.isPending}
              >
                Save Changes
              </Button>
            </Space>
            {isPushedToXero && (
              <Button
                icon={<SyncOutlined />}
                onClick={() => pushToXeroMutation.mutate()}
                loading={pushToXeroMutation.isPending}
              >
                Re-sync to Xero
              </Button>
            )}
          </Space>
        </Form>
      ) : (
        <div>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: spacing.xl }}>
              <Spin />
            </div>
          ) : history.length === 0 ? (
            <Text type="secondary">No edit history available</Text>
          ) : (
            <Timeline
              items={history.map((entry: HistoryEntry) => ({
                color: entry.action.includes('deleted') ? 'red' : 
                       entry.action.includes('uploaded') ? 'green' : 'blue',
                children: (
                  <div>
                    <Space>
                      <Tag>{formatAction(entry.action)}</Tag>
                      <Text type="secondary">
                        {dayjs(entry.createdAt).format('MMM D, YYYY h:mm A')}
                      </Text>
                    </Space>
                    {entry.user && (
                      <div>
                        <Text type="secondary">by {entry.user.name || entry.user.email}</Text>
                      </div>
                    )}
                    {entry.changes && Object.keys(entry.changes).length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {formatChanges(entry.changes)}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default CheckEditModal;
